// frontend/src/AccessibilityAudit/AccessibilityAudit.jsx — /accessibility-audit/:site: WCAG 2.1 AA scans of our Shopify
// storefronts, one tab per website. Spec: docs/superpowers/specs/2026-11-03-accessibility-audit-design.md.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { a11yApi } from './apiClient';
import SiteReport from './SiteReport';
import './AccessibilityAudit.css';

const POLL_MS = 10000;
const LAST_TAB = 'a11y-audit-site';

export default function AccessibilityAudit() {
  const { site: siteParam } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const runningIds = useRef('');

  const loadSites = useCallback(() => a11yApi.sites().then((d) => { setData(d); setError(''); return d; }, (e) => setError(e.message)), []);
  useEffect(() => { loadSites(); }, [loadSites]);

  const sites = useMemo(() => (data ? data.sites : []), [data]);
  const current = useMemo(() => (data ? data.current : []), [data]);
  let remembered = null;
  try { remembered = localStorage.getItem(LAST_TAB); } catch { /* private window */ }
  const site = sites.find((s) => s.id === siteParam) || sites.find((s) => s.id === remembered) || sites[0];
  useEffect(() => {
    if (!site) return;
    if (site.id !== siteParam) navigate(`/accessibility-audit/${site.id}`, { replace: true });
    try { localStorage.setItem(LAST_TAB, site.id); } catch { /* fine */ }
  }, [site, siteParam, navigate]);

  // While a scan is queued or running, ask every 10 s (only when the tab is visible). When one finishes, reload.
  useEffect(() => {
    const ids = current.map((s) => `${s.id}:${s.status}`).join(',');
    if (runningIds.current && runningIds.current !== ids) setReloadKey((k) => k + 1);
    runningIds.current = ids;
    if (!current.length) return undefined;
    const t = setInterval(() => { if (document.visibilityState === 'visible') loadSites(); }, POLL_MS);
    return () => clearInterval(t);
  }, [current, loadSites]);

  const busy = current.length > 0;
  const act = (p, done) => p.then((r) => { setMsg(done(r)); return loadSites(); }, (e) => setMsg(e.message));
  const onScan = (id) => act(a11yApi.scan(id), () => 'Scan started. It takes 20 to 30 minutes; you can leave this page.');
  const onAll = () => act(a11yApi.scanAll(), (r) => `Scanning ${r.scan_ids.length} websites one after another (a few hours).`);
  const onCancel = (id) => act(a11yApi.cancel(id), (r) => `Cancelled. Pages already scanned are kept${r.cancelled > 1 ? `; ${r.cancelled - 1} queued scans dropped` : ''}.`);
  const badge = (s) => {
    const c = s.latest && s.latest.counts && s.latest.counts.both;
    return c ? <span className="cnt" title="Critical and serious elements">{(c.critical || 0) + (c.serious || 0)}</span> : <span className="cnt none">–</span>;
  };

  return (
    <section className="uh-page uh-ax">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Accessibility Audit</h1>
          <div className="uh-sub">WCAG 2.1 AA checks of our Shopify storefronts, with the elements to fix. Scanned every night on desktop and phone.</div>
        </div>
        <button type="button" className="uh-btn" disabled={!data || busy} onClick={onAll}>
          {busy ? `Scanning ${(sites.find((s) => s.id === (current.find((x) => x.status === 'running') || current[0]).site_id) || {}).title || ''}…` : 'Scan all websites'}
        </button>
      </div>
      {error && <div className="uh-alert crit"><div>{error}</div></div>}
      {msg && <div className="uh-alert info"><div>{msg}</div><button type="button" className="uh-link" onClick={() => setMsg('')}>Dismiss</button></div>}
      {data && (
        <div className="uh-card">
          <div className="uh-tabs uh-ax-tabs" role="tablist" aria-label="Websites">
            {sites.map((s) => (
              <button type="button" role="tab" key={s.id} id={`uh-ax-tab-${s.id}`} aria-selected={site && s.id === site.id}
                onClick={() => navigate(`/accessibility-audit/${s.id}`)}>{s.title} {badge(s)}</button>
            ))}
          </div>
        </div>
      )}
      {!data && !error && <div className="uh-card"><div className="uh-empty">Loading…</div></div>}
      {site && (
        <div role="tabpanel" aria-labelledby={`uh-ax-tab-${site.id}`} className="uh-col">
          <SiteReport site={site} running={current} busy={busy} onScan={onScan} onCancel={onCancel} reloadKey={reloadKey} />
        </div>
      )}
    </section>
  );
}
